import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollToPlugin);
}

const NAV_SECTIONS = [
  { id: 'about', number: '01', label: 'À propos' },
  { id: 'services', number: '02', label: 'Offres' },
  { id: 'technologies', number: '03', label: 'Tech' },
  { id: 'projects', number: '04', label: 'Projets' },
  { id: 'contact', number: '05', label: 'Contact' },
] as const;

const ScrollIndicator: React.FC = () => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () =>
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.innerWidth <= 768;

    const updateMobile = () => setIsMobile(checkMobile());
    updateMobile();
    window.addEventListener('resize', updateMobile);

    const handleScroll = () => {
      const hero = document.getElementById('hero');
      if (hero) {
        const heroBottom = hero.getBoundingClientRect().bottom;
        setVisible(heroBottom < window.innerHeight * 0.55);
      }

      const viewportMark = window.innerHeight * 0.35;
      let current: string | null = null;

      NAV_SECTIONS.forEach((section) => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportMark && rect.bottom > viewportMark) {
          current = section.id;
        }
      });

      if (current) setActiveId(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    const timeout = setTimeout(handleScroll, 400);

    return () => {
      window.removeEventListener('resize', updateMobile);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (!section) return;
    const anchor = section.querySelector(`#${id}-anchor`);
    const target = anchor || section;

    gsap.to(window, {
      duration: 0.85,
      scrollTo: { y: target, offsetY: 0, autoKill: true },
      ease: 'power1.out',
    });
  };

  if (isMobile) return null;

  const activeIndex = NAV_SECTIONS.findIndex((s) => s.id === activeId);

  return (
    <AnimatePresence>
      {visible && (
        <motion.nav
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.35 }}
          className="fixed left-5 xl:left-8 top-1/2 -translate-y-1/2 z-[90] hidden md:flex flex-col items-start"
          aria-label="Navigation des sections"
        >
          <div className="relative flex flex-col gap-5 pl-3">
            {/* Rail */}
            <span className="absolute left-0 top-1 bottom-1 w-px bg-gray-300/80" />
            <motion.span
              className="absolute left-0 top-1 w-px bg-[#FFE600] origin-top"
              animate={{
                height:
                  activeIndex >= 0
                    ? `calc(${(activeIndex / Math.max(NAV_SECTIONS.length - 1, 1)) * 100}% + 8px)`
                    : '0%',
              }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />

            {NAV_SECTIONS.map((section) => {
              const isActive = section.id === activeId;

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  className="group relative flex items-center gap-3 text-left bg-transparent border-0 cursor-pointer p-0"
                  aria-current={isActive ? 'true' : undefined}
                >
                  <span
                    className={`absolute -left-[15px] w-2 h-2 rounded-full transition-colors duration-300 ${
                      isActive ? 'bg-[#FFE600] scale-125' : 'bg-gray-300 group-hover:bg-gray-400'
                    }`}
                  />
                  <span
                    className={`font-montserrat text-xs tracking-[0.2em] font-semibold transition-colors duration-300 ${
                      isActive ? 'text-[#0e1c36]' : 'text-[#0e1c36]/35 group-hover:text-[#0e1c36]/60'
                    }`}
                  >
                    {section.number}
                  </span>
                  <span
                    className={`font-montserrat text-[11px] tracking-wide transition-all duration-300 ${
                      isActive
                        ? 'opacity-100 text-[#0e1c36] translate-x-0'
                        : 'opacity-0 -translate-x-1 group-hover:opacity-70 group-hover:translate-x-0 text-gray-500'
                    }`}
                  >
                    {section.label}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};

export default ScrollIndicator;
